import { useState } from 'react';
import { ToolLayout } from '../../components/tools/ToolLayout';
import { MiniLineChart } from '../../components/tools/MiniLineChart';
import { formatBrl } from '../../data/coins';

type Compounding = 'sem' | 'mensal' | 'diaria';

interface StakingResult {
  finalBalance: number;
  earnings: number;
  effectiveAnnualPct: number;
  monthlyBalances: number[];
  monthLabels: string[];
}

export const CalculadoraStakingPage: React.FC = () => {
  const [principal, setPrincipal] = useState('1000');
  const [aprPct, setAprPct] = useState('5');
  const [months, setMonths] = useState('12');
  const [compounding, setCompounding] = useState<Compounding>('mensal');
  const [result, setResult] = useState<StakingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculate = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    const p = parseFloat(principal.replace(',', '.'));
    const apr = parseFloat(aprPct.replace(',', '.'));
    const n = parseInt(months, 10);

    if (!Number.isFinite(p) || p <= 0) {
      setError('Informe um valor válido.');
      return;
    }
    if (!Number.isFinite(apr) || apr < 0 || apr > 1000) {
      setError('Informe uma taxa anual entre 0% e 1000%.');
      return;
    }
    if (!Number.isFinite(n) || n < 1 || n > 120) {
      setError('O prazo deve ser entre 1 e 120 meses.');
      return;
    }

    const r = apr / 100;
    const balanceAtMonth = (month: number): number => {
      const years = month / 12;
      if (compounding === 'sem') return p * (1 + r * years);
      const periodsPerYear = compounding === 'mensal' ? 12 : 365;
      return p * Math.pow(1 + r / periodsPerYear, periodsPerYear * years);
    };

    const monthlyBalances: number[] = [];
    const monthLabels: string[] = [];
    for (let m = 0; m <= n; m++) {
      monthlyBalances.push(balanceAtMonth(m));
      monthLabels.push(`${m}m`);
    }

    const finalBalance = monthlyBalances[n];
    setResult({
      finalBalance,
      earnings: finalBalance - p,
      effectiveAnnualPct: (Math.pow(finalBalance / p, 12 / n) - 1) * 100,
      monthlyBalances,
      monthLabels,
    });
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500';

  return (
    <ToolLayout
      path="/ferramentas/calculadora-staking"
      title="Calculadora de Staking"
      metaTitle="Calculadora de Staking e Rendimento Composto"
      metaDescription="Estime o rendimento de staking de criptomoedas: valor inicial, taxa anual (APR), prazo e frequência de composição, com gráfico de evolução do saldo."
      intro={[
        'Staking é o processo de bloquear criptomoedas para ajudar a validar transações em redes proof-of-stake, recebendo recompensas em troca. As taxas anunciadas (APR) variam por rede, por validador e ao longo do tempo.',
        'Esta calculadora estima a evolução de um valor em staking a partir de uma taxa anual informada por você, com ou sem composição dos rendimentos (compounding).',
      ]}
      methodology={[
        'Sem composição: juros simples — saldo final = valor × (1 + taxa × anos).',
        'Com composição mensal ou diária: saldo final = valor × (1 + taxa/n)^(n × anos), onde n é o número de períodos por ano.',
        'A taxa informada é considerada constante durante todo o prazo — na prática, as taxas de staking mudam com a participação da rede.',
        'Recompensas de staking podem ser tributáveis; a simulação não considera impostos nem taxas de validadores/exchanges.',
      ]}
      faqs={[
        {
          question: 'De onde vem a taxa de staking (APR)?',
          answer:
            'Cada rede e provedor divulga a própria taxa — por exemplo, na página oficial do protocolo, em exchanges ou em validadores. Informe a taxa anunciada pelo serviço que você pretende usar. Desconfie de taxas muito acima da média do mercado.',
        },
        {
          question: 'O rendimento é garantido?',
          answer:
            'Não. As taxas variam com a participação total da rede, o desempenho do validador e as regras do protocolo. Além disso, o preço do ativo em reais pode cair mais do que o rendimento em cripto — o resultado em BRL pode ser negativo.',
        },
        {
          question: 'Qual a diferença entre APR e APY?',
          answer:
            'APR é a taxa anual sem composição; APY considera os juros sobre juros. Nesta calculadora, escolher composição diária ou mensal aproxima o resultado do APY correspondente ao APR informado.',
        },
      ]}
    >
      <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
        <form onSubmit={calculate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Valor (R$)</span>
            <input type="number" min="1" step="any" value={principal} onChange={(e) => setPrincipal(e.target.value)} className={inputClass} required />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Taxa anual — APR (%)</span>
            <input type="number" min="0" max="1000" step="any" value={aprPct} onChange={(e) => setAprPct(e.target.value)} className={inputClass} required />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Prazo (meses)</span>
            <input type="number" min="1" max="120" value={months} onChange={(e) => setMonths(e.target.value)} className={inputClass} required />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Composição</span>
            <select value={compounding} onChange={(e) => setCompounding(e.target.value as Compounding)} className={inputClass}>
              <option value="sem">Sem composição</option>
              <option value="mensal">Mensal</option>
              <option value="diaria">Diária</option>
            </select>
          </label>
          <button type="submit" className="sm:col-span-2 lg:col-span-4 bg-blue-600 text-white font-semibold rounded-lg px-6 py-2.5 hover:bg-blue-700 transition">
            Calcular
          </button>
        </form>

        {error && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</p>
        )}

        {result && (
          <div className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Rendimento estimado</p>
                <p className="font-bold text-green-600">{formatBrl(result.earnings, 2)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Saldo final estimado</p>
                <p className="font-bold text-gray-900">{formatBrl(result.finalBalance, 2)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Taxa anual efetiva (APY)</p>
                <p className="font-bold text-gray-900">{result.effectiveAnnualPct.toFixed(2)}%</p>
              </div>
            </div>

            <MiniLineChart
              series={[{ label: 'Saldo estimado', values: result.monthlyBalances, color: '#2563eb' }]}
              xLabels={result.monthLabels}
              formatValue={(v) => formatBrl(v)}
            />
            <p className="text-xs text-gray-400 mt-3">
              Estimativa com taxa constante informada pelo usuário. Taxas reais de staking variam ao longo do
              tempo e o rendimento não é garantido.
            </p>
          </div>
        )}
      </div>
    </ToolLayout>
  );
};
